import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { ALL_LOCATIONS } from '@/src/contexts/LocationsContext'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import { mapToEnergyTypeDto } from '@/src/features/procurement-and-markets/details/types/adjustment.utils'
import { useLoadPlanning } from '@/src/features/procurement-and-markets/hooks/useLoadPlanningsV2.hook'
import { useLoadPlanningCurrentLocation } from '@/src/features/procurement-and-markets/load-planning/contexts/LoadPlanningCurrentLocationContext'
import {
  useLoadPlanningCurve,
  useLoadedStrategyMetadata,
} from '@/src/features/procurement-and-markets/load-planning/hooks/LoadPlanning.hooks'
import type { AdjustmentRow } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/table/adjustmentTable.types'
import { getAdjustmentWarnings } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/table/adjustmentTable.utils'
import { mapLoadPlanningUnitText } from '@/src/features/procurement-and-markets/load-planning/load-planning-graph/LoadPlanning.utils'
import { useMemo } from 'react'

export const useAdjustmentConsumptionInjector = ({ adjustments }: { adjustments: AdjustmentRow[] }) => {
  const { data: strategy } = useLoadedStrategyMetadata()
  const { companyPgId: companyId } = useLocationsContext()
  const { locationId } = useLoadPlanningCurrentLocation()

  const { data: loadPlanning } = useLoadPlanning(companyId, strategy?.year, strategy?.energyType)
  const hasCustomReferenceBaseLoad = !!loadPlanning?.hasCustomReferenceBaseLoad

  const { data: loadForecastData, isLoading } = useLoadPlanningCurve({
    companyId,
    locationId: locationId !== ALL_LOCATIONS ? locationId : undefined,
    year: strategy ? Number(strategy.year) : undefined,
    energyType: mapToEnergyTypeDto(strategy?.energyType),
    granularity: GranularityEnum.ONE_MONTH,
  })

  const unit = loadForecastData?.monthlyData[0].savedValue.energy.unit

  const consumptionMap: Record<string, number> | undefined = useMemo(() => {
    if (!loadForecastData) return undefined

    const bucket: Record<string, number> = {}

    for (const dataPoint of loadForecastData.monthlyData) {
      dataPoint.adjustmentDeltas.forEach(adjustment => {
        const adjustmentDelta = adjustment.draftDeltaValue
          ? adjustment.draftDeltaValue.energy.amount
          : adjustment.savedDeltaValue?.energy.amount ?? 0

        bucket[adjustment.adjustmentId] = (bucket[adjustment.adjustmentId] ?? 0) + adjustmentDelta
      })
    }

    return bucket
  }, [loadForecastData])

  if (!consumptionMap) {
    return { adjustments, isInjecting: isLoading }
  }

  const injectedAdjustments = adjustments.map(adjustment => ({
    ...adjustment,
    consumptionDelta: consumptionMap[adjustment.id] ?? undefined,
    unit: mapLoadPlanningUnitText(unit),
    warnings: getAdjustmentWarnings(adjustment, adjustments, hasCustomReferenceBaseLoad),
  }))

  return {
    adjustments: injectedAdjustments,
    isInjecting: isLoading,
  }
}
